import React from 'react';

/**
 * Brand marks for the delivery-platform picker.
 *
 * Each glyph is a single path on a 24×24 viewBox, drawn in `currentColor`
 * so the caller controls the fill. The coloured tile behind the glyph lives
 * in `PlatformLogo` below.
 *
 * Note on trademarks: these are third-party marks used nominatively, to say
 * "we deliver over this service". Each company publishes brand guidelines
 * (colour values, clear space, no recolouring) — worth a read before launch.
 */

type GlyphProps = { size?: number };

export function WhatsAppGlyph({ size = 18 }: GlyphProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347M12.05 21.785h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.002-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413" />
    </svg>
  );
}

export function SnapchatGlyph({ size = 18 }: GlyphProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
      <path d="M12.166 1.5c2.94 0 4.982 2.2 5.09 5.14.036.79.01 1.44-.02 2.03-.01.19.01.3.16.34.34.09.79-.05 1.12-.18.4-.16.86.01.99.42.13.42-.1.8-.63 1.06-.26.13-.6.25-.9.38-.36.15-.62.27-.62.5 0 .17.1.42.27.74.6 1.1 1.72 2.28 3.1 2.73.4.13.58.5.46.87-.2.63-1.22 1.06-2.4 1.28-.13.02-.2.1-.23.25-.05.23-.1.5-.2.8-.1.3-.33.44-.7.44-.16 0-.35-.02-.57-.06a5.8 5.8 0 0 0-1.14-.11c-.28 0-.57.02-.87.08-.63.12-1.16.5-1.73.9-.74.52-1.5 1.06-2.65 1.06-1.15 0-1.9-.54-2.64-1.06-.57-.4-1.1-.78-1.73-.9a4.8 4.8 0 0 0-.87-.08c-.42 0-.8.05-1.14.11-.22.04-.4.06-.57.06-.37 0-.6-.14-.7-.44-.1-.3-.15-.57-.2-.8-.03-.15-.1-.23-.23-.25-1.18-.22-2.2-.65-2.4-1.28-.12-.37.06-.74.46-.87 1.38-.45 2.5-1.63 3.1-2.73.17-.32.27-.57.27-.74 0-.23-.26-.35-.62-.5-.3-.13-.64-.25-.9-.38-.53-.26-.76-.64-.63-1.06.13-.41.59-.58.99-.42.33.13.78.27 1.12.18.15-.04.17-.15.16-.34-.03-.59-.056-1.24-.02-2.03C7.02 3.7 9.06 1.5 12 1.5z" />
    </svg>
  );
}

export function TelegramGlyph({ size = 18 }: GlyphProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
      <path d="M23.91 3.79 20.3 20.84c-.25 1.21-.98 1.5-2 .94l-5.5-4.07-2.66 2.57c-.3.3-.55.56-1.1.56-.72 0-.6-.27-.84-.95L6.3 13.7l-5.45-1.7c-1.18-.35-1.19-1.16.26-1.75l21.26-8.2c.97-.43 1.9.24 1.53 1.73z" />
    </svg>
  );
}

export function InstagramGlyph({ size = 18 }: GlyphProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
      <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0m0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07s-3.586-.015-4.859-.074c-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844s.016-3.586.061-4.861c.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06zm0 3.678a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324M12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8m7.846-10.405a1.441 1.441 0 0 1-2.88 0 1.44 1.44 0 0 1 2.88 0" />
    </svg>
  );
}

// TikTok's mark is three layers of the same note: a cyan shadow, a red shadow
// and the white note on top (drawn in `currentColor` like the other glyphs).
// Colours are TikTok's own: cyan #25F4EE, red #FE2C55, on black. The viewBox is
// a little wider than 24×24 so the offset shadows are not clipped at the edge.
const TIKTOK_NOTE =
  'M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z';

export function TikTokGlyph({ size = 18 }: GlyphProps) {
  return (
    <svg width={size} height={size} viewBox="-1 -1 26 26" fill="currentColor" aria-hidden="true" focusable="false" style={{ overflow: 'visible' }}>
      <path d={TIKTOK_NOTE} fill="#25F4EE" transform="translate(-0.85 -0.85)" />
      <path d={TIKTOK_NOTE} fill="#FE2C55" transform="translate(0.85 0.85)" />
      <path d={TIKTOK_NOTE} />
    </svg>
  );
}

export function SmsGlyph({ size = 18 }: GlyphProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  );
}

// ── Tile ─────────────────────────────────────────────────────────────────────

export type PlatformId = 'WhatsApp' | 'Snapchat' | 'Telegram' | 'Instagram' | 'TikTok' | 'SMS';

const TILES: Record<PlatformId, { bg: string; fg: string; ring: string; Glyph: React.FC<GlyphProps> }> = {
  WhatsApp: { bg: '#25D366', fg: '#ffffff', ring: 'rgba(37,211,102,.35)', Glyph: WhatsAppGlyph },
  Snapchat: { bg: '#FFFC00', fg: '#0b0b0b', ring: 'rgba(255,252,0,.45)', Glyph: SnapchatGlyph },
  Telegram: { bg: 'linear-gradient(180deg,#37BBFE,#007DBB)', fg: '#ffffff', ring: 'rgba(0,136,204,.35)', Glyph: TelegramGlyph },
  Instagram: {
    bg: 'radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%)',
    fg: '#ffffff',
    ring: 'rgba(214,36,159,.35)',
    Glyph: InstagramGlyph,
  },
  TikTok: { bg: '#000000', fg: '#ffffff', ring: 'rgba(37,244,238,.30)', Glyph: TikTokGlyph },
  SMS: { bg: 'linear-gradient(180deg,#4b5563,#27303d)', fg: '#ffffff', ring: 'rgba(75,85,99,.35)', Glyph: SmsGlyph },
};

export function PlatformLogo({ platform, size = 34, active = true }: { platform: PlatformId; size?: number; active?: boolean }) {
  const tile = TILES[platform];
  const Glyph = tile.Glyph;
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: size * 0.3,
        background: tile.bg,
        color: tile.fg,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        boxShadow: active ? `0 2px 10px ${tile.ring}` : 'none',
        filter: active ? 'none' : 'saturate(.25)',
        transition: 'box-shadow .18s ease, filter .18s ease',
      }}
    >
      <Glyph size={Math.round(size * 0.55)} />
    </span>
  );
}

export default PlatformLogo;
